'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createClient } from '@/lib/supabase/client';
import type { User } from '@supabase/supabase-js';
import { Plus, Loader2, Star, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useActiveWatchlist } from '@/lib/hooks/useActiveWatchlist';
import {
  BASTION_GOLD,
  BASTION_GOLD_BORDER,
  BASTION_GOLD_RGB,
  BASTION_GOLD_SOFT,
  BASTION_GOLD_SOFT_HOVER,
} from '@/lib/bastion-accent';
import SubscriptionModal from './SubscriptionModal';

/**
 * Lightweight watchlist metadata used by QuickAddButton. The parent component
 * is expected to keep this in sync with the user's actual watchlists (id,
 * name, and uppercase ticker membership).
 */
export interface WatchlistMeta {
  id: string;
  name: string;
  tickers: Set<string>;
}

interface QuickAddButtonProps {
  ticker: string;
  fullTicker?: string;
  companyName?: string;
  user: User | null;
  /** All of the current user's watchlists with full metadata. */
  watchlists: WatchlistMeta[];
  /** Called after add/remove operations so the parent can refresh state. */
  onChanged?: () => void | Promise<void>;
  /** Called when the user is not authenticated and clicks the button. */
  onUnauthorized?: () => void;
  /** Sizing variant. */
  size?: 'sm' | 'md';
  /** Icon-only (default) or labeled pill button. */
  variant?: 'icon' | 'pill';
  /** If true, both + and ★ are hidden until parent hover (via [data-quickadd-hover]). */
  hideUntilHover?: boolean;
  /** If true, the + button is always visible (not just on hover). */
  alwaysShowPlus?: boolean;
}

const POPOVER_W = 250;
const POPOVER_GAP = 6;
const POPOVER_VIEWPORT_PAD = 8;

function clampPopoverPos(rect: DOMRect, itemCount: number): { x: number; y: number } {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;

  let x = rect.left + rect.width / 2;
  const halfW = POPOVER_W / 2;
  if (x - halfW < POPOVER_VIEWPORT_PAD) x = halfW + POPOVER_VIEWPORT_PAD;
  if (x + halfW > vw - POPOVER_VIEWPORT_PAD) x = vw - halfW - POPOVER_VIEWPORT_PAD;

  // header(40) + items(36 each) + padding(16), capped at max-height
  const estimatedH = Math.min(40 + itemCount * 36 + 16, 340);

  const spaceBelow = vh - rect.bottom - POPOVER_GAP;
  const spaceAbove = rect.top - POPOVER_GAP;

  let y: number;
  if (spaceBelow >= estimatedH || spaceBelow >= spaceAbove) {
    y = rect.bottom + POPOVER_GAP;
  } else {
    y = rect.top - POPOVER_GAP - estimatedH;
    if (y < POPOVER_VIEWPORT_PAD) y = POPOVER_VIEWPORT_PAD;
  }

  return { x, y };
}

const ICON_BG_BASE = 'rgba(255, 255, 255, 0.08)';
const ICON_BG_HOVER = 'rgba(255, 255, 255, 0.16)';
/** Soft gold tile behind filled / outline watchlist stars (not porcelain). */
const ICON_BG_ACTIVE = BASTION_GOLD_SOFT;
const ACCENT_GOLD = BASTION_GOLD;

export default function QuickAddButton({
  ticker,
  fullTicker,
  companyName,
  user,
  watchlists = [],
  onChanged,
  onUnauthorized,
  size = 'sm',
  variant = 'icon',
  hideUntilHover = false,
  alwaysShowPlus = false,
}: QuickAddButtonProps) {
  const tickerKey = (fullTicker || ticker || '').toUpperCase();
  const watchlistIds = useMemo(() => watchlists.map((w) => w.id), [watchlists]);
  const { activeId, setActive } = useActiveWatchlist(watchlistIds);

  const memberWatchlistIds = useMemo(() => {
    const set = new Set<string>();
    for (const wl of watchlists) {
      if (wl.tickers.has(tickerKey)) set.add(wl.id);
    }
    return set;
  }, [watchlists, tickerKey]);

  const isMember = memberWatchlistIds.size > 0;

  // Popover order: lists that already hold this ticker first, so removal is one click away.
  const popoverWatchlists = useMemo(
    () => [...watchlists].sort((a, b) => Number(memberWatchlistIds.has(b.id)) - Number(memberWatchlistIds.has(a.id))),
    [watchlists, memberWatchlistIds],
  );

  const [busy, setBusy] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState<{ x: number; y: number } | null>(null);
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [subModalMessage, setSubModalMessage] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const checkRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!popoverOpen) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('[data-quickadd-popover]')) return;
      if (t.closest('[data-quickadd-button]')) return;
      setPopoverOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPopoverOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [popoverOpen]);

  const ensureAuth = (): boolean => {
    if (user) return true;
    if (onUnauthorized) onUnauthorized();
    else window.location.href = '/subscription';
    return false;
  };

  const callAddItem = useCallback(async (watchlistId: string): Promise<'added' | 'duplicate' | 'limit' | 'unauthorized' | 'error'> => {
    try {
      const res = await fetch('/api/watchlists/add-item', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          watchlistId,
          ticker: tickerKey,
          companyName: companyName || ticker,
        }),
      });
      if (res.ok) return 'added';
      if (res.status === 409) return 'duplicate';
      if (res.status === 401) return 'unauthorized';
      const err = await res.json().catch(() => null);
      if (res.status === 400 && err?.error?.toLowerCase?.().includes('limit')) return 'limit';
      console.error('Add to watchlist failed:', err);
      return 'error';
    } catch (e) {
      console.error('Add to watchlist threw:', e);
      return 'error';
    }
  }, [tickerKey, companyName, ticker]);

  const showLimitModal = (watchlistName: string) => {
    setSubModalMessage(
      `Couldn't add ${companyName || ticker} to "${watchlistName}" — it's full (5 of 5 on Free). Premium unlocks 100 stocks per list, 10 watchlists, and Bastion factor scores across all your holdings.`
    );
    setSubModalOpen(true);
  };

  const addToList = useCallback(async (watchlistId: string) => {
    const wl = watchlists.find((w) => w.id === watchlistId);
    if (!wl) return;
    setBusy(true);
    const result = await callAddItem(watchlistId);
    setBusy(false);
    if (result === 'added' || result === 'duplicate') {
      setActive(watchlistId);
      await onChanged?.();
      if (result === 'added') window.dispatchEvent(new CustomEvent('watchlist-changed', { detail: { delta: 1 } }));
      const label = ticker || companyName;
      if (result === 'added') {
        toast.success(`Added ${label} to "${wl.name}"`);
      } else {
        toast.message(`${label} is already in "${wl.name}"`);
      }
    } else if (result === 'limit') {
      showLimitModal(wl.name);
    } else if (result === 'unauthorized') {
      onUnauthorized?.();
    } else {
      toast.error('Could not add to watchlist. Please try again.');
    }
  }, [watchlists, callAddItem, setActive, onChanged, ticker, companyName, onUnauthorized]);

  const handlePlusClick = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!ensureAuth()) return;

    // No watchlists yet — create a default one and add.
    if (watchlists.length === 0) {
      setBusy(true);
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('watchlists')
          .insert({ user_id: user!.id, name: 'General Watchlist', is_public: false })
          .select()
          .single();
        if (error || !data) throw error;
        const result = await callAddItem(data.id);
        if (result === 'added' || result === 'duplicate') {
          setActive(data.id);
          await onChanged?.();
          if (result === 'added') window.dispatchEvent(new CustomEvent('watchlist-changed', { detail: { delta: 1 } }));
          toast.success(`Added ${ticker || companyName} to "General Watchlist"`);
        } else if (result === 'limit') {
          showLimitModal('General Watchlist');
        } else if (result === 'unauthorized') {
          onUnauthorized?.();
        } else {
          toast.error('Could not add to watchlist.');
        }
      } catch (err) {
        console.error('Failed to create default watchlist:', err);
        toast.error('Could not create watchlist.');
      } finally {
        setBusy(false);
      }
      return;
    }

    // Single watchlist — add directly.
    if (watchlists.length === 1) {
      await addToList(watchlists[0].id);
      return;
    }

    // Multiple watchlists — open popover for explicit choice.
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setPopoverPos(clampPopoverPos(rect, watchlists.length));
    setPopoverOpen(true);
  }, [watchlists, addToList, callAddItem, setActive, onChanged, ticker, companyName, user]);

  const handleStarClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!ensureAuth()) return;
    const rect = (checkRef.current || (e.currentTarget as HTMLElement)).getBoundingClientRect();
    setPopoverPos(clampPopoverPos(rect, watchlists.length));
    setPopoverOpen(true);
  }, [watchlists.length]);

  const removeFromList = useCallback(async (watchlistId: string) => {
    const wl = watchlists.find((w) => w.id === watchlistId);
    if (!wl) return;
    setBusy(true);
    try {
      const supabase = createClient();
      const { error } = await supabase
        .from('watchlist_items')
        .delete()
        .eq('ticker', tickerKey)
        .eq('watchlist_id', watchlistId);
      if (error) {
        toast.error('Could not remove from watchlist.');
        return;
      }
      await onChanged?.();
      window.dispatchEvent(new CustomEvent('watchlist-changed', { detail: { delta: -1 } }));
      toast.success(`Removed ${ticker || companyName} from "${wl.name}"`);
    } catch {
      toast.error('Could not remove from watchlist.');
    } finally {
      setBusy(false);
    }
  }, [watchlists, tickerKey, onChanged, ticker, companyName]);

  const dim = size === 'md' ? 22 : 18;
  const iconSize = size === 'md' ? 14 : 12;

  if (variant === 'pill') {
    return (
      <>
        <button
          type="button"
          data-quickadd-button
          onClick={isMember ? handleStarClick : handlePlusClick}
          disabled={busy}
          className={`quickadd-pill${isMember ? ' quickadd-pill-star' : ' quickadd-pill-plus'}`}
        >
          {busy ? (
            <Loader2 size={14} className="quickadd-spin" />
          ) : isMember ? (
            <Star size={14} strokeWidth={1.6} fill={ACCENT_GOLD} color={ACCENT_GOLD} />
          ) : (
            <Plus size={14} strokeWidth={2.5} />
          )}
          {isMember ? 'In Watchlist' : 'Add to Watchlist'}
        </button>

        {popoverOpen && popoverPos && typeof document !== 'undefined' && createPortal(
          <div
            data-quickadd-popover
            className="quickadd-popover"
            style={{ left: popoverPos.x, top: popoverPos.y }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="quickadd-popover-header">
              <span className="quickadd-popover-title">{ticker || companyName}</span>
              <a
                href="/watchlist"
                target="_blank"
                rel="noopener noreferrer"
                className="quickadd-popover-open"
                onClick={() => setPopoverOpen(false)}
              >
                <Star size={12} strokeWidth={1.8} />
                Watchlists
                <ExternalLink size={10} strokeWidth={1.6} style={{ opacity: 0.5 }} />
              </a>
            </div>
            <div className="quickadd-popover-list">
              {popoverWatchlists.map((wl) => {
                const inThis = wl.tickers.has(tickerKey);
                return (
                  <button
                    key={wl.id}
                    type="button"
                    className={`quickadd-popover-item${inThis ? ' quickadd-popover-item-in' : ''}`}
                    disabled={busy}
                    onClick={async () => {
                      setPopoverOpen(false);
                      if (inThis) {
                        await removeFromList(wl.id);
                      } else {
                        await addToList(wl.id);
                      }
                    }}
                  >
                    <span className="quickadd-popover-item-name">{wl.name}</span>
                    {inThis
                      ? <Star size={11} strokeWidth={1.6} fill={ACCENT_GOLD} color={ACCENT_GOLD} />
                      : <Plus size={11} strokeWidth={2.5} />}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )}

        <SubscriptionModal
          isOpen={subModalOpen}
          onClose={() => { setSubModalOpen(false); setSubModalMessage(null); }}
          message={subModalMessage}
        />

        <style jsx global>{`
          .quickadd-pill {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 4px 10px;
            border-radius: 6px;
            border: 1px solid transparent;
            font-size: 12px;
            font-weight: 500;
            font-family: var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif;
            cursor: pointer;
            white-space: nowrap;
            flex-shrink: 0;
            transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease, transform 0.05s ease;
          }
          .quickadd-pill:active {
            transform: scale(0.98);
          }
          .quickadd-pill:disabled {
            cursor: default;
            opacity: 0.6;
          }
          .quickadd-pill-plus {
            background: ${ICON_BG_BASE};
            color: rgba(255, 255, 255, 0.8);
          }
          .quickadd-pill-plus:hover {
            background: ${ICON_BG_HOVER};
            color: #ffffff;
          }
          .quickadd-pill-star {
            background: transparent;
            color: rgba(255, 255, 255, 0.8);
          }
          .quickadd-pill-star:hover {
            background: ${ICON_BG_ACTIVE};
            border-color: ${BASTION_GOLD_BORDER};
          }
          @keyframes quickadd-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .quickadd-spin {
            animation: quickadd-spin 0.8s linear infinite;
          }
          .quickadd-popover {
            position: fixed;
            transform: translateX(-50%);
            min-width: 220px;
            max-width: 280px;
            background: #1d1d1d;
            border: 1px solid rgba(255, 255, 255, 0.10);
            border-radius: 10px;
            box-shadow: 0 12px 28px rgba(0, 0, 0, 0.48);
            padding: 8px 6px;
            z-index: 10000;
            font-family: var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif;
          }
          .quickadd-popover-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 4px 10px 8px 10px;
          }
          .quickadd-popover-title {
            font-size: 11px;
            font-weight: 600;
            color: rgba(255, 255, 255, 0.5);
            text-transform: uppercase;
            letter-spacing: 0.04em;
          }
          .quickadd-popover-open {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            padding: 4px 11px;
            border-radius: 6px;
            font-size: 11px;
            font-weight: 500;
            color: rgba(255, 255, 255, 0.65);
            background: rgba(255, 255, 255, 0.08);
            border: 1px solid rgba(255, 255, 255, 0.18);
            text-decoration: none;
            letter-spacing: 0.01em;
            white-space: nowrap;
            transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
          }
          .quickadd-popover-open:hover {
            color: rgba(255, 255, 255, 0.85);
            background: rgba(255, 255, 255, 0.14);
            border-color: rgba(255, 255, 255, 0.35);
          }
          .quickadd-popover-list {
            display: flex;
            flex-direction: column;
            gap: 2px;
            max-height: 280px;
            overflow-y: auto;
            overscroll-behavior: contain;
            scrollbar-width: thin;
            scrollbar-color: #3d3d3d #1a1a1a;
          }
          .quickadd-popover-list::-webkit-scrollbar {
            width: 6px;
          }
          .quickadd-popover-list::-webkit-scrollbar-track {
            background: #1a1a1a;
          }
          .quickadd-popover-list::-webkit-scrollbar-thumb {
            background: #3d3d3d;
            border-radius: 3px;
          }
          .quickadd-popover-list::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
          .quickadd-popover-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            width: 100%;
            padding: 8px 10px;
            border: none;
            border-radius: 6px;
            background: transparent;
            color: rgba(255, 255, 255, 0.85);
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            text-align: left;
            transition: background 0.12s ease;
          }
          .quickadd-popover-item:hover {
            background: rgba(255, 255, 255, 0.08);
          }
          .quickadd-popover-item-in {
            color: ${ACCENT_GOLD};
          }
          .quickadd-popover-item-name {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        `}</style>
      </>
    );
  }

  return (
    <>
      <div
        ref={containerRef}
        className="quickadd-root"
        data-quickadd-root
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
        onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
      >
        {isMember ? (
          <button
            ref={checkRef}
            type="button"
            data-quickadd-button
            onClick={handleStarClick}
            disabled={busy}
            className={`quickadd-btn quickadd-btn-star${hideUntilHover ? ' quickadd-btn-ghost' : ''}`}
            style={{ width: dim, height: dim }}
          >
            {busy ? (
              <Loader2 size={iconSize} className="quickadd-spin" />
            ) : (
              <Star size={iconSize} strokeWidth={1.6} color={ACCENT_GOLD} />
            )}
          </button>
        ) : (
          <button
            type="button"
            data-quickadd-button
            onClick={handlePlusClick}
            disabled={busy}
            className={`quickadd-btn${alwaysShowPlus ? ' quickadd-btn-plus-always' : ' quickadd-btn-plus'}${hideUntilHover ? ' quickadd-btn-ghost' : ''}`}
            style={{ width: dim, height: dim }}
          >
            {busy ? <Loader2 size={iconSize} className="quickadd-spin" /> : <Plus size={iconSize} strokeWidth={2.5} />}
          </button>
        )}
      </div>

      {popoverOpen && popoverPos && typeof document !== 'undefined' && createPortal(
        <div
          data-quickadd-popover
          className="quickadd-popover"
          style={{ left: popoverPos.x, top: popoverPos.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="quickadd-popover-header">
            <span className="quickadd-popover-title">{ticker || companyName}</span>
            <a
              href="/watchlist"
              target="_blank"
              rel="noopener noreferrer"
              className="quickadd-popover-open"
              onClick={() => setPopoverOpen(false)}
            >
              <Star size={12} strokeWidth={1.8} />
              Watchlists
              <ExternalLink size={10} strokeWidth={1.6} style={{ opacity: 0.5 }} />
            </a>
          </div>
          <div className="quickadd-popover-list">
            {popoverWatchlists.map((wl) => {
              const inThis = wl.tickers.has(tickerKey);
              return (
                <button
                  key={wl.id}
                  type="button"
                  className={`quickadd-popover-item${inThis ? ' quickadd-popover-item-in' : ''}`}
                  disabled={busy}
                  onClick={async () => {
                    setPopoverOpen(false);
                    if (inThis) {
                      await removeFromList(wl.id);
                    } else {
                      await addToList(wl.id);
                    }
                  }}
                >
                  <span className="quickadd-popover-item-name">{wl.name}</span>
                  {inThis
                    ? <Star size={11} strokeWidth={1.6} fill={ACCENT_GOLD} color={ACCENT_GOLD} />
                    : <Plus size={11} strokeWidth={2.5} />}
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}

      <SubscriptionModal
        isOpen={subModalOpen}
        onClose={() => { setSubModalOpen(false); setSubModalMessage(null); }}
        message={subModalMessage}
      />

      <style jsx global>{`
        .quickadd-root {
          display: inline-flex;
          align-items: center;
        }
        .quickadd-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          border: 1px solid transparent;
          background: transparent;
          color: rgba(255, 255, 255, 0.8);
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease, transform 0.05s ease;
          padding: 0;
        }
        .quickadd-btn:active {
          transform: scale(0.9);
        }
        .quickadd-btn:disabled {
          cursor: default;
          opacity: 0.6;
        }
        /* Plus: hidden by default, visible on hover (own or any ancestor with [data-quickadd-hover]) */
        .quickadd-btn-plus {
          opacity: 0;
        }
        .quickadd-root:hover .quickadd-btn-plus,
        [data-quickadd-hover]:hover .quickadd-btn-plus,
        .quickadd-btn-plus:focus-visible {
          opacity: 1;
          background: ${ICON_BG_BASE};
        }
        .quickadd-btn-plus:hover {
          background: ${ICON_BG_HOVER} !important;
          color: #ffffff;
        }
        /* Plus always visible variant */
        .quickadd-btn-plus-always {
          opacity: 1;
          background: ${ICON_BG_BASE};
        }
        .quickadd-btn-plus-always:hover {
          background: ${ICON_BG_HOVER} !important;
          color: #ffffff;
        }
        /* Star: outline + soft gold tile, like portfolio tag */
        .quickadd-btn-star {
          opacity: 1;
          background: ${BASTION_GOLD_SOFT};
          border-color: transparent;
        }
        [data-quickadd-hover]:hover .quickadd-btn-star,
        .quickadd-btn-star:hover {
          background: ${BASTION_GOLD_SOFT_HOVER};
          border-color: ${BASTION_GOLD_BORDER};
        }
        .quickadd-btn-star:hover {
          background: rgba(${BASTION_GOLD_RGB}, 0.26);
        }
        /* Ghost: everything hidden until ancestor hover (both + and ★) */
        .quickadd-btn-ghost {
          opacity: 0;
        }
        .quickadd-root:hover .quickadd-btn-ghost,
        [data-quickadd-hover]:hover .quickadd-btn-ghost,
        .quickadd-btn-ghost:focus-visible {
          opacity: 1;
        }
        @keyframes quickadd-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .quickadd-spin {
          animation: quickadd-spin 0.8s linear infinite;
        }
        .quickadd-popover {
          position: fixed;
          transform: translateX(-50%);
          min-width: 220px;
          max-width: 280px;
          background: #1d1d1d;
          border: 1px solid rgba(255, 255, 255, 0.10);
          border-radius: 10px;
          box-shadow: 0 12px 28px rgba(0, 0, 0, 0.48);
          padding: 8px 6px;
          z-index: 10000;
          font-family: var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif;
        }
        .quickadd-popover-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 4px 10px 8px 10px;
        }
        .quickadd-popover-title {
          font-size: 11px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.5);
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .quickadd-popover-open {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 4px 11px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.65);
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.18);
          text-decoration: none;
          letter-spacing: 0.01em;
          white-space: nowrap;
          transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
        }
        .quickadd-popover-open:hover {
          color: rgba(255, 255, 255, 0.85);
          background: rgba(255, 255, 255, 0.14);
          border-color: rgba(255, 255, 255, 0.35);
        }
        .quickadd-popover-list {
          display: flex;
          flex-direction: column;
          gap: 1px;
          max-height: 280px;
          overflow-y: auto;
          overscroll-behavior: contain;
          scrollbar-width: thin;
          scrollbar-color: #3d3d3d #1a1a1a;
        }
        .quickadd-popover-list::-webkit-scrollbar {
          width: 6px;
        }
        .quickadd-popover-list::-webkit-scrollbar-track {
          background: #1a1a1a;
        }
        .quickadd-popover-list::-webkit-scrollbar-thumb {
          background: #3d3d3d;
          border-radius: 3px;
        }
        .quickadd-popover-list::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
        .quickadd-popover-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          width: 100%;
          background: transparent;
          border: none;
          padding: 7px 10px;
          border-radius: 6px;
          color: rgba(255, 255, 255, 0.85);
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          text-align: left;
          font-family: inherit;
          transition: background 0.12s ease, color 0.12s ease;
        }
        .quickadd-popover-item:hover {
          background: rgba(255, 255, 255, 0.06);
          color: #ffffff;
        }
        .quickadd-popover-item-in {
          color: ${ACCENT_GOLD};
        }
        .quickadd-popover-item-in:hover {
          background: rgba(239, 68, 68, 0.08);
          color: #f87171;
        }
        .quickadd-popover-item-name {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex: 1;
        }
      `}</style>
    </>
  );
}
